// The composer's rules and labels. Pure — everything here is decided before a request is made.

export const FORMATS = [
  { id: 'both', label: 'Image + content' },
  { id: 'text', label: 'Only content' },
  { id: 'img', label: 'Only image' },
];

// Where the pop-up sits on screen. The content is identical either way — this picks the container,
// which is what decides how much of an interruption it is.
export const PLACEMENTS = [
  { id: 'center', label: 'Centred' },
  { id: 'sheet', label: 'Bottom sheet' },
];

export const GENDERS = [
  { id: 'all', label: 'Everyone' },
  { id: 'Female', label: 'Women' },
  { id: 'Male', label: 'Men' },
];

// Only the triggers the user document can actually date. 'Selfie verified' is missing because
// nothing stamps the approval time, so a delay could not be measured from it.
export const TRIGGERS = [
  { id: 'signup_completed', label: 'Sign-up completed' },
  { id: 'first_message', label: 'First message sent' },
  { id: 'inactive_7d', label: 'Inactive for 7 days' },
];

export const DELAYS = [
  { hours: 0, label: 'Immediately' },
  { hours: 1, label: 'After 1 hour' },
  { hours: 2, label: 'After 2 hours' },
  { hours: 24, label: 'After 1 day' },
  { hours: 48, label: 'After 2 days' },
];

export const formatLabel = (id) => FORMATS.find((f) => f.id === id)?.label || id;
export const placementLabel = (id) => PLACEMENTS.find((p) => p.id === id)?.label || 'Centred';

/* ── the crop ─────────────────────────────────────────────────────────────────────────────────
   The pop-up's image box is a fixed 16:10 and `object-fit: cover` anchors at 50% 50%, which takes
   the face off every portrait photo. The admin drags the design to say what stays in frame; what
   is stored is that anchor, as two percentages, which is `object-position` verbatim.            */

const clamp = (n) => Math.min(100, Math.max(0, n));

/** Top-anchored, because a face is nearer the top of a photo than the middle of one. */
export const DEFAULT_FOCUS = { x: 50, y: 0 };

// Every pop-up ends in a button to the app's plans screen; this is its label until the admin types one.
export const DEFAULT_CTA = 'Upgrade now';

/** Anything at all — a legacy chip id, a half-written object, nothing — becomes a usable anchor. */
export const normaliseFocus = (focus) => {
  if (typeof focus === 'string') {
    return { x: 50, y: { center: 50, bottom: 100 }[focus] ?? 0 };
  }
  const x = Number(focus?.x);
  const y = Number(focus?.y);
  return {
    x: Number.isFinite(x) ? clamp(x) : DEFAULT_FOCUS.x,
    y: Number.isFinite(y) ? clamp(y) : DEFAULT_FOCUS.y,
  };
};

export const focusPosition = (focus) => {
  const { x, y } = normaliseFocus(focus);
  return `${x}% ${y}%`;
};

/**
 * Where a drag leaves the anchor.
 *
 * `cover` scales the design until it fills the box, so what is hidden is the overflow on each axis
 * — and moving the design by exactly that overflow in pixels is the whole 0-100% travel. Dragging
 * DOWN reveals what was above, which is the anchor moving toward 0, hence the subtraction.
 *
 * An axis with no overflow does not move: a 16:10 design fills a 16:10 box exactly, and there is
 * nothing off-screen to drag into view. Without this it would slide against a divide-by-zero.
 */
export function dragFocus({ focus, dx, dy, box, natural }) {
  const from = normaliseFocus(focus);
  if (!natural?.w || !natural?.h || !box?.w || !box?.h) return from;
  const cover = Math.max(box.w / natural.w, box.h / natural.h);
  const shift = (delta, overflow, start) =>
    overflow > 0 ? clamp(start - (delta / overflow) * 100) : start;
  return {
    x: shift(dx, natural.w * cover - box.w, from.x),
    y: shift(dy, natural.h * cover - box.h, from.y),
  };
}
export const triggerLabel = (id) => TRIGGERS.find((t) => t.id === id)?.label || '—';
export const delayLabel = (hours) =>
  DELAYS.find((d) => d.hours === Number(hours))?.label || `After ${hours}h`;

// Mirrors the server's own validation so a launch that cannot succeed never leaves the page.
export const composeError = (draft) => {
  const { format, image, title, body, mode, members = [] } = draft;
  if (format !== 'text' && !image) return 'Upload the design first.';
  if (format !== 'img' && !title?.trim() && !body?.trim()) {
    return 'Write the headline or content first.';
  }
  if (mode === 'members' && members.length === 0) return 'Add at least one member first.';
  return null;
};

export const templateError = (tmpl) => {
  if (!tmpl.name?.trim()) return 'Name the template first.';
  if (!tmpl.title?.trim() && !tmpl.body?.trim() && !tmpl.image) {
    return 'Write the template’s text or upload its design first.';
  }
  return null;
};

// A template has no format chips — its format follows what was filled in.
export const templateFormat = ({ title, body, image }) => {
  const hasText = !!(title?.trim() || body?.trim());
  if (image && hasText) return 'both';
  return image ? 'img' : 'text';
};

/** "women · New Delhi, India" — the sentence under the reach number. */
export const audienceLabel = ({ mode, gender, country, city, members = [] }) => {
  if (mode === 'members') {
    return `${members.length} selected member${members.length === 1 ? '' : 's'}`;
  }
  const who = GENDERS.find((g) => g.id === gender)?.label.toLowerCase() || 'everyone';
  if (country === 'all') return `${who} · all countries`;
  return city ? `${who} · ${city}, ${country}` : `${who} · ${country} · all cities`;
};

/** A campaign runs until it is switched off or its end date passes. */
export const isLive = (campaign, now = new Date()) =>
  !!campaign?.active && (!campaign.endsAt || new Date(campaign.endsAt) >= now);

/** Share of the audience it launched at that has seen it at least once. */
export const reachPct = (campaign) => {
  if (!campaign?.audienceSize) return 0;
  return Math.min(100, Math.round(((campaign.unique || 0) / campaign.audienceSize) * 100));
};

/** Week-on-week movement for a tile. null when there is no previous week to compare against. */
export const deltaPct = (current, previous) => {
  if (!previous) return null;
  return Math.round(((current - previous) / previous) * 100);
};

export const num = (n) => Number(n || 0).toLocaleString();

export const shortDate = (value) =>
  value ? new Date(value).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '—';

/** The seen report names a campaign by its headline, or by the fact that it is only art. */
export const campaignName = (campaign) => {
  if (campaign.format === 'img') return '📷 Image pop-up';
  const text = campaign.title?.trim() || campaign.body?.trim() || '';
  return `“${text.slice(0, 40)}”`;
};
